import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import Text from 'components/Text';
import { PRODUCTS_REPORT_HEIGHT } from './styles';
import reportSelectors from 'features/report/services/selectors';
import { useMemo } from 'react';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import Box from 'components/Box';
import clsx from 'clsx';

interface IProductData {
  key: string;
  product: string;
  discount: number;
  sales: number;
}

const ProductsTable = () => {
  const dataProductReportV2 = reportSelectors.getDataProductReportV2();
  const data = useMemo(() => {
    return dataProductReportV2.map((item) => {
      return {
        key: item.id,
        product: item.categoryName,
        discount: item.discount,
        sales: item.sale,
      };
    });
  }, [dataProductReportV2]);

  const totalDiscount = useMemo(() => {
    return data.reduce((acc, item) => acc + item.discount, 0);
  }, [data]);

  const totalSales = useMemo(() => {
    return data.reduce((acc, item) => acc + item.sales, 0);
  }, [data]);

  return (
    <Container>
      <AutoScaleContainer>
        <div className='wrapper-list'>
          <CategoriesList>
            {dataProductReportV2.map(o => (
              <CategoryItem key={o.id}>
                <RowValue highlight label={o.categoryName}>{formatCurrency(o.sale)}</RowValue>
                {o.products.map(pro => (
                  <RowValue key={pro.productId} label={pro.productName} subValue={`-${formatCurrency(pro.discount)}`}>{formatCurrency(pro.totalSale)}</RowValue>
                ))}
              </CategoryItem>
            ))}
          </CategoriesList>
        </div>
      </AutoScaleContainer>
      <TotalFooter>
        <RowValue totalDiscount label='Total Discount'>-{formatCurrency(totalDiscount)}</RowValue>
        <RowValue totalSales label='Total Sales' >{formatCurrency(totalSales)}</RowValue>
      </TotalFooter>
    </Container>
  );
};

export default ProductsTable;

const Container = styled.div`
  display: flex;
  width: 404px;
  padding-top: 16px;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  border-radius: 4px;
  background: #CBD5E1;
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
`;
const AutoScaleContainer = styled.div`
  flex: 1;
  position: relative;
  align-self: stretch;
  .wrapper-list {
    position: absolute;
    inset: 0;
    overflow: auto;
  } 
`;
const CategoriesList = styled.div`
  display: flex;
  padding: 0 8px;
  padding-bottom: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
`;
const TotalFooter = styled.div`
  display: flex;
  padding: 8px 8px 16px 8px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  background: #FFF;
  box-shadow: 0 -4px 9px 0 rgba(0, 0, 0, 0.15);
`;

const CategoryItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;
type RowValueProps = {
  label?: string;
  subValue?: string;
  children?: any;
  highlight?: boolean;
  totalDiscount?: boolean;
  totalSales?: boolean;
};
const RowValue = ({ highlight, label, subValue, children, totalDiscount, totalSales }: RowValueProps) => {
  return (
    <RowValueContainer className={clsx(highlight && 'highlight', totalDiscount && 'total-discount', totalSales && 'total-sales')}>
      <p className='item-text item-label'>{label}</p>
      {!!subValue && <p className='item-text item-value item-sub-value'>{subValue}</p>}
      <p className='item-text item-value item-children'>{children}</p>
    </RowValueContainer>
  );
};

const RowValueContainer = styled(Box)`
display: flex;
align-self: stretch;
align-items: center;
.item-text {
  color: #1D2129;
  font-family: Poppins;
  font-size: 18px;
  font-weight: 600;
  line-height: 30px;
  font-style: normal;
  text-transform: capitalize;
}
.item-label {
  flex: 1;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  color: #1D2129;
  text-overflow: ellipsis;
  text-align: left;
}
.item-value {
  text-align: right;
}

.item-children {
  flex: 0.5;
}

&.highlight {
  display: flex;
  height: 32px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  background: #232F3E;
  .item-text {
    color: #fff;
    font-size: 20px;
  }
  .item-value {
    font-size: 24px;
  }
  .item-children {
    flex: unset;
  }
}
&.total-discount {
  background: #FDF4CE;
  .item-text {
    font-size: 24px;
  }
}
&.total-sales {
  background: #232F3E;
  .item-text {
    color: #fff;
    font-size: 24px;
  }
}
`;